import React, { useEffect } from 'react';
import CustomModal from '../../baseComponents/gui/customModal/CustomModal';
import { useModal } from '../../../hooks/useModal';
import Scrollbar from 'react-scrollbars-custom';
import CustomButton from '../../features/customButton/ui/main/CustomButton';
import classNames from 'classnames';
import { safeHTML } from '../../../utils/safeHTML';
import { image } from '../../../utils/data/baseUrl';
import { GTMQuizCopyPromo } from '../../../constants/analytics/GTM';
import { useCopy } from '../../../hooks/useCopy';
import { useTranslation } from '../../../translations';
import { isNumber } from 'lodash';
import Product from '../../features/product';
import Back from '../../features/back/Back';
import QuizWinModalReward from './quizWinModalReward/QuizWinModalReward';
import Reward from '../../baseComponents/gui/reward/Reward';
import { productContent, compilationScrollbarSettings } from '../../../constants/copyright';
import { useProducts } from '../../../redux/reducer/content';
import useGetProductsAndClear from '../../features/product/model/hooks/useGetProductsAndClear';
import AppLoader from '../../features/appLoader/AppLoader';
import { useRequests } from '../../../redux/reducer/requests';

const { selection } = productContent;

const QuizWinModal = React.forwardRef(function (
  {
    className,
    subtitle,
    buttons,
    selectionTitle,
    selectionProducts,
    isFromGA = true,
    result,
    isBack,
    img,
    money,
    gacha_event_cash,
    reward_bonuses,
    promocodeData,
    promocodeButton,
    attempts,
    link,
    rewards,
    level,
  },
  ref,
) {
  const {
    authData: { isWeb },
  } = useRequests();
  const { promocode, rules, description } = promocodeData ?? {};
  const { productsFromGA } = useProducts();
  const [getProducts, isPendingProducts] = useGetProductsAndClear();
  const { id, closeModal } = useModal();
  const { t } = useTranslation();
  const [text, copy, isDisabled] = useCopy(promocode);
  const copyPromocode = () => {
    copy();
    GTMQuizCopyPromo(id);
  };

  useEffect(() => {
    if (selectionProducts && isFromGA) getProducts(selectionProducts.map((prod) => prod.sku));
  }, []);

  return (
    <CustomModal animation={['fade', 'scale', 'scale-bounce']} closeOnOverlayClick={false}>
      <div
        className={classNames(className, 'quiz-win-modal', {
          'quiz-win-modal_buttons-fixed': !!productsFromGA?.length,
          'quiz-win-modal_app': !isWeb,
        })}
        ref={ref}
      >
        <div className={'quiz-win-modal__bg'} />

        {isBack && <Back onClick={() => closeModal(id)} />}

        <div className={'quiz-win-modal__content'}>
          <Scrollbar {...compilationScrollbarSettings}>
            {img && (
              <div className='quiz-win-modal__image'>
                <img src={img} alt={''} />
              </div>
            )}

            {!!rewards?.length && (
              <div className={'quiz-win-modal__rewards'}>
                {rewards?.map(({ type, value }, index) => (
                  <Reward type={type} value={value} mod={'price'} key={`reward-${index}`} />
                ))}
              </div>
            )}

            {isNumber(level) && <div className={'quiz-win-modal__level'}>{`${t('other.level')} ${level}`}</div>}

            <h3 className={'quiz-win-modal__subtitle'}>{safeHTML(t(subtitle ?? 'other.taskComplete'))}</h3>

            {[gacha_event_cash, money, reward_bonuses, attempts].some(isNumber) && (
              <div className={'quiz-win-modal__prizes'}>
                <div className={'quiz-win-modal__prizes-list'}>
                  {reward_bonuses && <QuizWinModalReward type={'xp'} value={reward_bonuses} />}
                  {gacha_event_cash && <QuizWinModalReward type={'charm'} value={gacha_event_cash} />}
                  {money && <QuizWinModalReward type={'coin'} value={money} />}
                  {attempts && <QuizWinModalReward type={'energy'} value={attempts} />}
                </div>
              </div>
            )}

            {result && (
              <div className='quiz-win-modal__result'>
                {result.title && <div className='quiz-win-modal__result-title'>{safeHTML(t(result.title))}</div>}
                <div className='quiz-win-modal__result-items'>
                  <div className='quiz-win-modal__result-item'>
                    <img src={image('quiz/right.svg')} className={`quiz-win-modal__result-item-image`} alt={''} />
                    <div className={`quiz-win-modal__result-item-text`}>
                      {result.items.right} {t('other.correct')}
                    </div>
                  </div>
                  <div className='quiz-win-modal__result-item'>
                    <img src={image('quiz/wrong.svg')} className={`quiz-win-modal__result-item-image`} alt={''} />
                    <div className={`quiz-win-modal__result-item-text`}>
                      {result.items.wrong} {t('other.wrong')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {promocode && (
              <>
                <div className={'quiz-win-modal__promocode'}>
                  <span>{promocode}</span>
                  <CustomButton
                    disabled={isDisabled}
                    className={'custom-button_min custom-button_copy'}
                    text={text}
                    onClick={copyPromocode}
                  />
                </div>
                {description && (
                  <div className={'quiz-win-modal__promocode-description'}>{safeHTML(t(description))}</div>
                )}
                {rules && (
                  <a href={rules} {...link.attr}>
                    {safeHTML(t(link.text))}
                  </a>
                )}
                {/*{promocodeLink && <CustomButton {...promocodeButton.attr} href={promocodeLink}/>}*/}
              </>
            )}

            {!!productsFromGA?.length && (
              <div className={classNames('quiz-win-modal__more')}>
                <img src={image('procedureResult/arrow.svg')} className={'procedure-result-modal__arrow'} alt={''} />

                <div className='procedure-result-modal__selection quiz-win-modal__selection quiz-win-modal__selection_products'>
                  <div className='procedure-result-modal__selection-title'>
                    {safeHTML(t(selectionTitle ? selectionTitle : selection.title))}
                  </div>
                  <div className='procedure-result-modal__selection-list quiz-win-modal__selection-list_products'>
                    {productsFromGA.map((item) => (
                      <Product {...item} key={item.id} className={'product_quiz product_quiz-gray'} noCode={true} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Scrollbar>

          {!!buttons?.length && (
            <div className={'quiz-win-modal__buttons'}>
              {buttons?.map((button, index) => (
                <CustomButton key={index} {...button.attr} />
              ))}
            </div>
          )}
        </div>
        {isPendingProducts && <AppLoader />}
      </div>
    </CustomModal>
  );
});
QuizWinModal.displayName = 'QuizWinModal';
export default QuizWinModal;
QuizWinModal.propTypes = {};
